import { useEffect, useRef, useState, type ChangeEvent, type ReactNode, type Ref } from 'react';
import type { Messages } from '@immo/i18n';
import { useIntl, type IntlShape } from 'react-intl';
import { useLoaderData, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import {
  Button,
  Input,
  Label,
  Modal,
  ReportActionRow,
  SandboxedHtmlViewer,
  Textarea,
  useToast,
  InfoBox,
} from '@immo/ui-kit';
import {
  valuationBlockLayout,
  isClassificationInFlight,
  CLASSIFICATION_POLL_MS,
  VALUATION_AREA_FIELD_KEYS,
  VALUATION_CURRENCY_FIELD_KEYS,
  VALUATION_DATE_FIELD_KEYS,
  VALUATION_PERCENT_FIELD_KEYS,
  VALUATION_PRICE_EXPECTATION_KINDS,
  VALUATION_RETURN_REASON_MAX_LENGTH,
  VALUATION_YEAR_FIELD_KEYS,
  ValuationSubmitForApprovalMissingDetailsSchema,
  ValuationRequestAlreadyTakenDetailsSchema,
  ValuationNotAwaitingReleaseDetailsSchema,
  type ValuationBlockFieldKey,
  type WireError,
  type GetAdminValuationRequestResponse,
  type ValuationPriceExpectation,
  type ValuationRecord,
  type ValuationReport,
  type ValuationReportKind,
  type ValuationRequestDetail,
  type ValuationRequestDocument,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { euroFormatter } from '@/lib/currency';
import { formatArea, formatPercent } from '@/lib/unit-format';
import { claimHolderLabel } from '@/lib/valuations/claim-holder-label';
import {
  uploadValuationReport,
  VALUATION_REPORT_DOCUMENT_CONTENT_TYPES,
  VALUATION_REPORT_HTML_CONTENT_TYPES,
  DocumentPolicyError,
} from '@/lib/media/upload-valuation-report';
import { downloadValuationDocument, previewValuationDocument } from '@/lib/media/read-valuation-document';
import {
  downloadValuationReport,
  downloadValuationReportHtml,
  fetchValuationReportHtml,
  previewValuationReport,
} from '@/lib/media/read-valuation-report';
import { RequirePermission, useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { InvestmentIcon } from '@/components/layout/icons';
import { DocumentClassificationBadge } from '@/components/document-classification-badge';
import { FormField } from '@/components/form';

// The base + context keys whose label lives under `detail.field`; every other
// key is a type-specific field labelled from the shared `detail.field-name`
// table. Mirrors the web-app preview split.
const BASE_FIELD_KEYS = new Set<ValuationBlockFieldKey>([
  'objectType',
  'usage',
  'address',
  'valuationReason',
  'timeHorizon',
  'desiredCompletion',
  'note',
]);

// Fields whose value is an enum token translated via a `detail.*` option table.
const ENUM_VALUE_PATH: Partial<Record<ValuationBlockFieldKey, string>> = {
  objectType: 'objectType',
  usage: 'usage',
  valuationReason: 'reason-enum',
  timeHorizon: 'timeHorizon',
  heatingType: 'heatingType',
  energyEfficiencyClass: 'energyEfficiencyClass',
  houseType: 'houseType',
  basement: 'basement',
  developmentPlan: 'developmentPlan',
  servicingStatus: 'servicingStatus',
  contaminationSuspicion: 'contaminationSuspicion',
  graduatedIndexRent: 'graduated-index-rent',
  unitBreakdown: 'unit-breakdown',
  kitchenQuality: 'quality',
  bathroomQuality: 'quality',
  flooringQuality: 'quality',
  windowsQuality: 'quality',
  kitchenCondition: 'condition',
  bathroomCondition: 'condition',
  flooringCondition: 'condition',
  windowsCondition: 'condition',
};

const BOOLEAN_KEYS = new Set<ValuationBlockFieldKey>([
  'hasLift',
  'positivePreliminaryEnquiry',
  'hasBuildingPermit',
  'hasExistingBuilding',
]);
// Every unit membership below is imported from the contract, never re-typed here:
// this surface was built as a copy of the customer Vorschau and inherited its
// missing units along with everything else, so each membership is declared in
// exactly one place and both surfaces read it (IMM-644, IMM-681).
const DATE_KEYS = new Set<ValuationBlockFieldKey>(VALUATION_DATE_FIELD_KEYS);

// Year fields are ordinal labels, not quantities — render bare, never through the
// grouped number formatter that would show `1904` as `1.904` (IMM-444).
const YEAR_KEYS = new Set<ValuationBlockFieldKey>(VALUATION_YEAR_FIELD_KEYS);

const CURRENCY_KEYS = new Set<ValuationBlockFieldKey>(VALUATION_CURRENCY_FIELD_KEYS);
const AREA_KEYS = new Set<ValuationBlockFieldKey>(VALUATION_AREA_FIELD_KEYS);
const PERCENT_KEYS = new Set<ValuationBlockFieldKey>(VALUATION_PERCENT_FIELD_KEYS);

export async function valuationRequestDetailLoader({
  params,
}: LoaderFunctionArgs): Promise<GetAdminValuationRequestResponse> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing valuation request id', { status: 404 });
  }

  const result = await apiClient.adminValuationRequests.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load valuation request', { status: result.status });
  }

  return result.body;
}

export function ValuationRequestDetailPage() {
  const t = useTranslations('pages.admin.valuations.detail');
  const tPriceKind = useTranslations('pages.admin.valuations.detail.priceExpectationKind');
  const tStatus = useTranslations('pages.admin.valuations.status');
  const tTitle = useTranslations('pages.admin.valuations');
  const intl = useIntl();
  const loaderData = useLoaderData() as GetAdminValuationRequestResponse;
  const { revalidate } = useRevalidator();

  // Local copy so take-on / submit-for-approval / release / return adopt the
  // returned Detail immediately; a revalidate (e.g. after a lost claim, or after a
  // reviewer action) re-seeds it from the loader. The delivered `record` only
  // appears once RELEASED, and only via a loader run (the command responses carry
  // the Detail alone), so it is read straight off the loader data rather than
  // mirrored into state.
  const [detail, setDetail] = useState<ValuationRequestDetail>(loaderData.request);
  const record = loaderData.record;

  useEffect(() => {
    setDetail(loaderData.request);
  }, [loaderData]);

  // While any Unterlage's Objekt document is still being auto-classified, re-read
  // the request so „Wird einsortiert …" resolves to the real category on its own —
  // the same self-refresh the agent's Unterlagen list does (IMM-501.3). A row with
  // no Objekt classification reports null and is NOT in flight, so a request of
  // only legacy attachments and images never starts one.
  //
  // Deliberately a direct read rather than `revalidate()`: revalidation re-runs
  // EVERY matched loader, including the protected layout's `requireSession`, which
  // signs the operator out on any transient failure — and this route has no
  // errorElement, so a failed detail load would replace the page with the error
  // screen. A background tick must never be able to do either. Swapping `detail`
  // in place also keeps the rendered rows mounted (BL-011).
  const requestId = detail.id;
  const hasClassifying = detail.documents.some((document) => isClassificationInFlight(document.classificationStatus));

  useEffect(() => {
    if (!hasClassifying) return;

    let cancelled = false;

    const handle = setInterval(() => {
      void (async () => {
        try {
          const result = await apiClient.adminValuationRequests.get({ pathParams: { id: requestId } });

          if (!cancelled && result.ok) setDetail(result.body.request);
        } catch {
          // A failed tick is not worth surfacing — the next one retries, and the
          // rendered rows keep showing what they already have.
        }
      })();
    }, CLASSIFICATION_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [hasClassifying, requestId]);

  const [takingOn, setTakingOn] = useState(false);
  const [takeOnError, setTakeOnError] = useState<string | null>(null);

  // „Übernahme aufheben" (IMM-572.2) — hands an IN_PROGRESS claim back to the
  // queue. The dialog lives at the page root, NOT inside the status-conditional
  // topbar slot: a 422 refusal revalidates the detail, which unmounts the
  // control — the open dialog must survive that to show its message.
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasingClaim, setReleasingClaim] = useState(false);
  const [releaseClaimError, setReleaseClaimError] = useState<string | null>(null);

  // The composed holder label (shared helper, bound to the PARENT namespace —
  // the two `admin-key-holder*` keys are unreachable from this page's `t`).
  const holderLabel = claimHolderLabel(detail.takenByKind, detail.takenByName, tTitle);

  const releaseClaim = async () => {
    setReleaseClaimError(null);
    setReleasingClaim(true);

    try {
      const result = await apiClient.adminValuationRequests.releaseClaim({ pathParams: { id: detail.id } });

      if (result.ok) {
        setDetail(result.body);
        setReleaseOpen(false);

        return;
      }

      // No longer claimed — permanent (BL-020). Name it and let the surface
      // catch up to the real state; the dialog stays open with the message.
      if (result.status === 422) {
        setReleaseClaimError(t('release-claim.not-claimed-error'));
        void revalidate();

        return;
      }

      setReleaseClaimError(t(result.status === 403 ? 'release-claim.forbidden-error' : 'release-claim.error'));
    } catch {
      setReleaseClaimError(t('release-claim.error'));
    } finally {
      setReleasingClaim(false);
    }
  };

  const blocks = valuationBlockLayout(detail.objectType, detail.usage, detail.unitBreakdown);

  const fieldLabel = (key: ValuationBlockFieldKey): string =>
    BASE_FIELD_KEYS.has(key) ? t(`field.${key}`) : t(`field-name.${key}`);

  const fieldValue = (key: ValuationBlockFieldKey): string => {
    const raw = (detail as Record<string, unknown>)[key];

    if (raw === null || raw === undefined || raw === '') return t('not-provided');

    const enumPath = ENUM_VALUE_PATH[key];

    if (enumPath !== undefined) return t(`${enumPath}.${String(raw)}`);

    if (BOOLEAN_KEYS.has(key)) return raw ? t('boolean-ja') : t('boolean-nein');

    if (DATE_KEYS.has(key) && typeof raw === 'string') return intl.formatDate(raw, { dateStyle: 'medium' });

    if (YEAR_KEYS.has(key) && typeof raw === 'number') return String(raw);

    // Before the generic numeric fallback: a monetary value is still a `number`,
    // so the fallback would swallow it and render the amount bare (IMM-644).
    if (CURRENCY_KEYS.has(key) && typeof raw === 'number') return euroFormatter.format(raw);

    if (AREA_KEYS.has(key) && typeof raw === 'number') return formatArea(raw);

    if (PERCENT_KEYS.has(key) && typeof raw === 'number') return formatPercent(raw);

    if (typeof raw === 'number') return intl.formatNumber(raw);

    return String(raw);
  };

  const priceExpectationValue = (entry: ValuationPriceExpectation | undefined): string => {
    if (entry === undefined || entry.type === 'NONE' || entry.from === null) return t('price-expectation-none');

    if (entry.type === 'FIXED_PRICE' || entry.to === null) return euroFormatter.format(entry.from);

    return `${euroFormatter.format(entry.from)} – ${euroFormatter.format(entry.to)}`;
  };

  // `priceExpectations` is a marker key, not a scalar: it expands into one row per
  // kind. The rows are driven off the kind constant rather than the stored array,
  // because the read model is a 0–3 row set — a kind the agent left unset has no
  // row at all and must still read as „Keine Angabe".
  const detailRows = (key: ValuationBlockFieldKey): DetailRow[] => {
    if (key !== 'priceExpectations') return [{ key, label: fieldLabel(key), value: fieldValue(key) }];

    const byKind = new Map(detail.priceExpectations.map((entry) => [entry.kind, entry]));

    return VALUATION_PRICE_EXPECTATION_KINDS.map((kind) => ({
      key: `priceExpectations.${kind}`,
      label: tPriceKind(kind),
      value: priceExpectationValue(byKind.get(kind)),
    }));
  };

  const takeOn = async () => {
    setTakingOn(true);
    setTakeOnError(null);

    try {
      const result = await apiClient.adminValuationRequests.takeOn({ pathParams: { id: detail.id } });

      if (result.ok) {
        setDetail(result.body);

        return;
      }

      // AC 41: another operator already holds it — name the assignee. Re-read
      // so the surface catches up to the real (IN_PROGRESS) state.
      if (result.status === 409) {
        const parsed = ValuationRequestAlreadyTakenDetailsSchema.safeParse(result.body.error.details);
        const holder = parsed.success ? parsed.data.takenByName : null;

        setTakeOnError(t('already-taken', { name: holder ?? tTitle('unassigned') }));
        void revalidate();

        return;
      }

      setTakeOnError(result.status === 403 ? t('take-on-forbidden') : t('take-on-error'));
    } catch {
      setTakeOnError(t('take-on-error'));
    } finally {
      setTakingOn(false);
    }
  };

  const title = detail.address ?? t('not-provided');

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={InvestmentIcon}
            crumbs={[{ label: t('back'), to: '/valuations' }, { label: title }]}
          />
        }
        trailing={
          detail.status === 'REQUESTED' ? (
            <RequirePermission permission="backoffice:valuation:valuation-request-take-on">
              <Button size="sm" onClick={() => void takeOn()} disabled={takingOn}>
                {takingOn ? t('taking-on') : t('take-on')}
              </Button>
            </RequirePermission>
          ) : detail.status === 'IN_PROGRESS' ? (
            // The mirror action of „Übernehmen" (IMM-572.2) — same slot, same
            // permission (the backend leaf declares one for both), absent rather
            // than disabled for any other status or a missing permission.
            <RequirePermission permission="backoffice:valuation:valuation-request-take-on">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setReleaseClaimError(null);
                  setReleaseOpen(true);
                }}
              >
                {t('release-claim.cta')}
              </Button>
            </RequirePermission>
          ) : null
        }
      />
      <div className="mx-auto w-full max-w-[880px] space-y-4 px-6 py-6">
        <header className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-border bg-surface px-5 py-4">
          <div className="min-w-0 space-y-1">
            <h1 className="text-xl font-semibold text-text-primary">{title}</h1>
            <p className="text-base-sm text-text-secondary">
              {t('requester-label')}: {detail.requestedByName ?? t('not-provided')}
              {detail.requestedAt !== null && ` · ${intl.formatDate(detail.requestedAt, { dateStyle: 'medium' })}`}
            </p>
            {detail.status === 'IN_PROGRESS' && (
              <p className="text-base-sm text-text-secondary">
                {t('assignee-label')}: {holderLabel ?? t('not-provided')}
                {detail.takenAt !== null && ` · ${intl.formatDate(detail.takenAt, { dateStyle: 'medium' })}`}
              </p>
            )}
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-2.5 py-0.5 text-base-sm text-text-secondary">
            <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" aria-hidden />
            {tStatus(detail.status)}
          </span>
        </header>

        {takeOnError !== null && <InfoBox type="danger">{takeOnError}</InfoBox>}

        {blocks.map((layoutBlock) => (
          <SectionCard key={layoutBlock.block} title={t(`block.${layoutBlock.block}`)}>
            {layoutBlock.block === 'parkingSpaces' ? (
              <DetailParkingSpaces detail={detail} t={t} intl={intl} />
            ) : layoutBlock.block === 'units' ? (
              <DetailUnits detail={detail} t={t} />
            ) : layoutBlock.block === 'documents' ? (
              <DetailDocuments detail={detail} t={t} intl={intl} />
            ) : (
              <>
                <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                  {layoutBlock.fieldKeys.flatMap(detailRows).map((row) => (
                    <div key={row.key} className="min-w-0">
                      <dt className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{row.label}</dt>
                      <dd className="mt-0.5 break-words text-base-sm text-text-primary">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                {layoutBlock.block === 'reason' && (
                  <p className="mt-3 text-base-sm text-text-tertiary">{t('price-expectation-note')}</p>
                )}
              </>
            )}
          </SectionCard>
        ))}

        {/* The image set is not part of the shared `valuationBlockLayout`, whose
            last block is `documents` — appending it here places Bilder directly
            below Unterlagen, the same placement the agent's Vorschau uses. */}
        <SectionCard title={t('block.images')}>
          <DetailImages detail={detail} t={t} intl={intl} />
        </SectionCard>

        {/* Operator production (IMM-407.5, renamed IMM-475.2). Shown for
            IN_PROGRESS behind the -complete permission; the backend enforces the
            assignee-only guard (the `me` id is the auth-provider id, not the admin
            principal id that `takenByAdminId` carries, so a client-side comparison
            is unreliable — we surface the backend's refusal instead). */}
        {detail.status === 'IN_PROGRESS' && (
          <RequirePermission permission="backoffice:valuation:valuation-request-complete">
            <SubmitForApprovalPanel
              detail={detail}
              t={t}
              intl={intl}
              onSubmitted={(submitted) => {
                setDetail(submitted);
                void revalidate();
              }}
            />
          </RequirePermission>
        )}

        {/* The approval gate (IMM-475.2). The held result reads read-only for every
            operator who can see the request; only the two reviewer actions sit
            behind the release permission. */}
        {detail.status === 'AWAITING_RELEASE' && (
          <AwaitingReleasePanel
            detail={detail}
            t={t}
            intl={intl}
            onReviewed={(reviewed) => {
              setDetail(reviewed);
              void revalidate();
            }}
          />
        )}

        {detail.status === 'RELEASED' && <ReleasedResult detail={detail} record={record} t={t} intl={intl} />}
      </div>

      {/* Mirrors the api-key revoke dialog (api-keys/detail.tsx). Mounted at the
          page root so a 422 → revalidate — which unmounts the topbar control —
          leaves the message readable. */}
      {releaseOpen && (
        <Modal
          open
          onClose={() => setReleaseOpen(false)}
          confirmOnClose={false}
          size="md"
          title={t('release-claim.confirm-title', { name: holderLabel ?? t('not-provided') })}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setReleaseOpen(false)} disabled={releasingClaim}>
                {t('release-claim.cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={() => void releaseClaim()} disabled={releasingClaim}>
                {releasingClaim ? t('release-claim.loading') : t('release-claim.confirm-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{t('release-claim.confirm-body')}</p>
          {releaseClaimError !== null && (
            <p role="alert" className="mt-3 text-sm text-danger-600">
              {releaseClaimError}
            </p>
          )}
        </Modal>
      )}
    </>
  );
}

// Native date input styled to match the ui-kit `Input` (which excludes `date`
// on purpose — there is no admin-app `DateInput` yet).
const DATE_INPUT_CLASS =
  'flex h-control-md w-full rounded-md border border-border bg-surface px-3 py-2 text-base font-sans text-text-primary transition-colors duration-micro ease-out focus-visible:outline-none focus-visible:border-interactive focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-60';

// The backend's `missing` keys we know how to label; anything else falls back to
// the raw key so an unmapped offender is still named rather than swallowed.
type SubmitMessages = Messages['pages']['admin']['valuations']['detail']['submit'];
type MissingKey = keyof SubmitMessages['missing-labels'];
type SubmitErrors = Record<'priceFrom' | 'priceTo' | 'report', string | null>;

const NO_SUBMIT_ERRORS: SubmitErrors = { priceFrom: null, priceTo: null, report: null };
const MISSING_FIELD_MAP = {
  priceFrom: { field: 'priceFrom', message: 'priceFrom' },
  priceTo: { field: 'priceTo', message: 'priceTo' },
  priceRange: { field: 'priceTo', message: 'priceRange' },
  report: { field: 'report', message: 'report' },
  reportHtmlMediaFileId: { field: 'report', message: 'report' },
  reportDocumentMediaFileId: { field: 'report', message: 'report' },
} as const satisfies Record<MissingKey, { field: keyof SubmitErrors; message: keyof SubmitMessages['field-errors'] }>;
const KNOWN_MISSING_KEYS = new Set<string>(Object.keys(MISSING_FIELD_MAP));

function missingFieldErrors(t: Translate, missing: readonly string[]): SubmitErrors | null {
  const errors = { ...NO_SUBMIT_ERRORS };

  for (const key of missing) {
    if (!KNOWN_MISSING_KEYS.has(key)) return null;

    const { field, message } = MISSING_FIELD_MAP[key as MissingKey];
    errors[field] ??= t(`submit.field-errors.${message}`);
  }

  return errors;
}

function parsePrice(raw: string): number | null {
  const trimmed = raw.trim();

  if (trimmed === '') return null;

  const value = Number(trimmed);

  return Number.isFinite(value) && value >= 0 ? value : null;
}

function missingLabel(t: Translate, key: string): string {
  return KNOWN_MISSING_KEYS.has(key) ? t(`submit.missing-labels.${key}`) : key;
}

// One held report slot's client state. `mediaFileId` is the confirmed media file
// the submit body carries for that kind; `filename` is what the operator sees.
type ReportSlotState = { readonly mediaFileId: string | null; readonly filename: string | null };

function heldReport(reports: ReadonlyArray<ValuationReport>, kind: ValuationReportKind): ReportSlotState {
  const held = reports.find((report) => report.kind === kind);

  return held === undefined
    ? { mediaFileId: null, filename: null }
    : { mediaFileId: held.mediaFileId, filename: held.originalFilename ?? held.mediaFileId };
}

// The produce panel (IMM-475.2). „Zur Freigabe einreichen" — it parks the result
// at the approval gate and delivers nothing. A request returned for rework keeps
// its held figures and reports, so the form opens pre-filled from them and shows
// the reviewer's „Grund der Rückgabe" above the inputs.
function SubmitForApprovalPanel({
  detail,
  t,
  intl,
  onSubmitted,
}: {
  readonly detail: ValuationRequestDetail;
  readonly t: Translate;
  readonly intl: IntlShape;
  readonly onSubmitted: (detail: ValuationRequestDetail) => void;
}) {
  const requestId = detail.id;
  const held = detail.heldResult;
  const todayIso = new Date().toISOString().slice(0, 10);

  const [priceFrom, setPriceFrom] = useState(held === null ? '' : String(held.priceFrom));
  const [priceTo, setPriceTo] = useState(held === null ? '' : String(held.priceTo));
  const [recommended, setRecommended] = useState(
    held === null || held.recommendedStartPrice === null ? '' : String(held.recommendedStartPrice),
  );
  const [valuationDate, setValuationDate] = useState(held === null ? todayIso : held.valuationDate);
  const [htmlReport, setHtmlReport] = useState<ReportSlotState>(heldReport(detail.reports, 'HTML'));
  const [documentReport, setDocumentReport] = useState<ReportSlotState>(heldReport(detail.reports, 'DOCUMENT'));
  const [submitting, setSubmitting] = useState(false);
  // IMM-576.1: each slot owns its upload, so the parent mirrors one in-flight flag per
  // slot and the submit is disabled while either is running. Two flags rather than one
  // counter: the slots are independent, and a counter would need decrement discipline in
  // the error path to avoid latching the button off.
  const [htmlUploading, setHtmlUploading] = useState(false);
  const [documentUploading, setDocumentUploading] = useState(false);
  const uploading = htmlUploading || documentUploading;
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<SubmitErrors>(NO_SUBMIT_ERRORS);
  const priceFromRef = useRef<HTMLInputElement>(null);
  const priceToRef = useRef<HTMLInputElement>(null);
  const reportRef = useRef<HTMLInputElement>(null);
  const focusPending = useRef(false);

  useEffect(() => {
    if (submitting || !focusPending.current) return;

    const input =
      errors.priceFrom !== null
        ? priceFromRef.current
        : errors.priceTo !== null
          ? priceToRef.current
          : errors.report !== null
            ? reportRef.current
            : null;

    // Refusals arrive while inputs are disabled; consume focus only after they re-enable.
    focusPending.current = false;
    input?.focus();
  }, [errors, submitting]);

  const clearError = (field: keyof SubmitErrors) =>
    setErrors((previous) => (previous[field] === null ? previous : { ...previous, [field]: null }));

  const refuseMissing = (missing: readonly string[]) => {
    const next = missingFieldErrors(t, missing);

    if (next === null) {
      setFormError(t('submit.missing-prefix', { list: missing.map((key) => missingLabel(t, key)).join(', ') }));

      return;
    }

    focusPending.current = true;
    setErrors(next);
  };

  const submit = async () => {
    setFormError(null);
    setErrors(NO_SUBMIT_ERRORS);
    focusPending.current = false;

    const from = parsePrice(priceFrom);
    const to = parsePrice(priceTo);

    // AC 42/43: the price range (from + to) and at least one report are required —
    // name any that are missing before touching the backend.
    const missing: MissingKey[] = [];

    if (from === null) missing.push('priceFrom');

    if (to === null) missing.push('priceTo');

    const noReport = htmlReport.mediaFileId === null && documentReport.mediaFileId === null;

    if (noReport) missing.push('report');

    if (from === null || to === null || noReport) {
      refuseMissing(missing);

      return;
    }

    setSubmitting(true);

    try {
      const result = await apiClient.adminValuationRequests.submitForApproval({
        pathParams: { id: requestId },
        body: {
          priceFrom: from,
          priceTo: to,
          recommendedStartPrice: parsePrice(recommended),
          valuationDate: valuationDate === '' ? null : valuationDate,
          reportHtmlMediaFileId: htmlReport.mediaFileId,
          reportDocumentMediaFileId: documentReport.mediaFileId,
        },
      });

      if (result.ok) {
        onSubmitted(result.body);

        return;
      }

      // AC 43: the backend refuses an invalid result (von > bis, no resolvable
      // report, …) with 422 `{ missing }`; name each offender back.
      if (result.status === 422) {
        const parsed = ValuationSubmitForApprovalMissingDetailsSchema.safeParse(result.body.error.details);

        if (parsed.success && parsed.data.missing.length > 0) {
          refuseMissing(parsed.data.missing);

          return;
        }
      }

      setFormError(t('submit.submit-error'));
    } catch {
      setFormError(t('submit.submit-error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Non-blocking advisory (IMM-468.1): the entered recommended start price sits
  // outside the entered price range. Derived live from current field state — the
  // out-of-range value no longer blocks submission, so this only informs.
  const recommendedFrom = parsePrice(priceFrom);
  const recommendedTo = parsePrice(priceTo);
  const recommendedValue = parsePrice(recommended);
  const recommendedOutOfRange =
    recommendedFrom !== null &&
    recommendedTo !== null &&
    recommendedValue !== null &&
    (recommendedValue < recommendedFrom || recommendedValue > recommendedTo);

  return (
    <SectionCard title={t('submit.title')}>
      <div className="space-y-4">
        {detail.returnReason !== null && (
          <div className="rounded-md bg-warning-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">
              {t('submit.returned-title')}
            </p>
            <p className="mt-0.5 break-words text-base-sm text-text-primary">{detail.returnReason}</p>
            {detail.returnedAt !== null && (
              <p className="mt-1 text-base-sm text-text-secondary">
                {t('submit.returned-by', {
                  name: detail.returnedByName ?? t('not-provided'),
                  date: intl.formatDate(detail.returnedAt, { dateStyle: 'medium' }),
                })}
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField id="submit-price-from" label={t('submit.price-from-label')} required error={errors.priceFrom}>
            {(errorId) => (
              <Input
                ref={priceFromRef}
                id="submit-price-from"
                type="number"
                inputMode="numeric"
                min={0}
                value={priceFrom}
                onChange={(e) => {
                  setPriceFrom(e.target.value);
                  clearError('priceFrom');
                }}
                disabled={submitting}
                required
                aria-required
                invalid={errors.priceFrom !== null}
                aria-describedby={errorId}
              />
            )}
          </FormField>
          <FormField id="submit-price-to" label={t('submit.price-to-label')} required error={errors.priceTo}>
            {(errorId) => (
              <Input
                ref={priceToRef}
                id="submit-price-to"
                type="number"
                inputMode="numeric"
                min={0}
                value={priceTo}
                onChange={(e) => {
                  setPriceTo(e.target.value);
                  clearError('priceTo');
                }}
                disabled={submitting}
                required
                aria-required
                invalid={errors.priceTo !== null}
                aria-describedby={errorId}
              />
            )}
          </FormField>
          <div className="space-y-1">
            <Label htmlFor="submit-recommended">{t('submit.recommended-label')}</Label>
            <Input
              id="submit-recommended"
              type="number"
              inputMode="numeric"
              min={0}
              value={recommended}
              onChange={(e) => setRecommended(e.target.value)}
              disabled={submitting}
            />
            {recommendedOutOfRange && (
              <p className="text-base-sm text-warning-700">{t('submit.recommended-out-of-range')}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="submit-date">{t('submit.date-label')}</Label>
            <input
              id="submit-date"
              type="date"
              className={DATE_INPUT_CLASS}
              value={valuationDate}
              onChange={(e) => setValuationDate(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        {/* Two independent slots, one per report kind: each presigns, uploads and
            confirms on its own, and each holds its own media file id. At least one
            of the two must carry a file. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ReportSlot
            inputId="submit-report-html"
            label={t('submit.report-html-label')}
            accepted={VALUATION_REPORT_HTML_CONTENT_TYPES}
            requestId={requestId}
            slot={htmlReport}
            onChanged={(slot) => {
              setHtmlReport(slot);

              if (slot.mediaFileId !== null || documentReport.mediaFileId !== null) clearError('report');
            }}
            errorId={errors.report === null ? undefined : 'submit-report-error'}
            inputRef={reportRef}
            onUploadingChange={setHtmlUploading}
            disabled={submitting}
            t={t}
          />
          <ReportSlot
            inputId="submit-report-document"
            label={t('submit.report-document-label')}
            accepted={VALUATION_REPORT_DOCUMENT_CONTENT_TYPES}
            requestId={requestId}
            slot={documentReport}
            onChanged={(slot) => {
              setDocumentReport(slot);

              if (slot.mediaFileId !== null || htmlReport.mediaFileId !== null) clearError('report');
            }}
            errorId={errors.report === null ? undefined : 'submit-report-error'}
            onUploadingChange={setDocumentUploading}
            disabled={submitting}
            t={t}
          />
        </div>
        {errors.report !== null && (
          <p id="submit-report-error" role="alert" className="text-sm text-danger-600">
            {errors.report}
          </p>
        )}
        <p className="text-base-sm text-text-tertiary">{t('submit.report-hint')}</p>

        {formError !== null && <InfoBox type="danger">{formError}</InfoBox>}

        <Button
          size="sm"
          onClick={() => void submit()}
          disabled={submitting || uploading}
          aria-describedby={uploading ? 'submit-upload-hint' : undefined}
        >
          {submitting ? t('submit.submitting') : t('submit.submit')}
        </Button>
        {uploading && (
          <p id="submit-upload-hint" className="text-base-sm text-text-tertiary">
            {t('submit.upload-in-progress-hint')}
          </p>
        )}
      </div>
    </SectionCard>
  );
}

// One report upload slot. The accepted content types are the slot's own — the HTML
// slot would otherwise reject its own file against the document set (IMM-475.2).
function ReportSlot({
  inputId,
  label,
  accepted,
  requestId,
  slot,
  onChanged,
  onUploadingChange,
  disabled,
  errorId,
  inputRef,
  t,
}: {
  readonly inputId: string;
  readonly label: string;
  readonly accepted: ReadonlyArray<string>;
  readonly requestId: string;
  readonly slot: ReportSlotState;
  readonly onChanged: (slot: ReportSlotState) => void;
  // IMM-576.1: the parent disables its submit while any slot upload is in flight, so the
  // slot reports every transition of its own flag — including the failure path.
  readonly onUploadingChange: (uploading: boolean) => void;
  readonly disabled: boolean;
  readonly errorId: string | undefined;
  readonly inputRef?: Ref<HTMLInputElement>;
  readonly t: Translate;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so re-selecting the same file re-fires onChange.
    event.target.value = '';

    if (file === undefined) return;

    setUploadError(null);
    setUploading(true);
    onUploadingChange(true);

    try {
      const uploaded = await uploadValuationReport(requestId, file, accepted);

      onChanged({ mediaFileId: uploaded.mediaFileId, filename: file.name });
    } catch (cause) {
      // AC 44: a disallowed / oversized / failed upload leaves this slot empty (no
      // media id held) and names a reason. The over-20-MB and wrong-type refusals
      // never reach the network — they carry the policy hint instead.
      onChanged({ mediaFileId: null, filename: null });
      setUploadError(t(cause instanceof DocumentPolicyError ? 'submit.upload-policy-error' : 'submit.upload-error'));
    } finally {
      // Also on the failure path: the operator must stay able to submit an already-held
      // report, or to retry, so the submit re-enables whether the upload settled or threw.
      setUploading(false);
      onUploadingChange(false);
    }
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={inputId}>{label}</Label>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={accepted.join(',')}
        onChange={(e) => void onPick(e)}
        disabled={uploading || disabled}
        aria-describedby={errorId}
        aria-invalid={errorId !== undefined || undefined}
        className="block w-full text-base-sm text-text-secondary file:mr-3 file:rounded-md file:border file:border-border file:bg-surface file:px-3 file:py-1.5 file:text-base-sm file:text-text-primary"
      />
      <p className="text-base-sm text-text-tertiary">
        {uploading
          ? t('submit.uploading')
          : slot.filename !== null
            ? t('submit.report-selected', { name: slot.filename })
            : t('submit.report-none')}
      </p>
      {uploadError !== null && (
        <p role="alert" className="text-base-sm text-danger-600">
          {uploadError}
        </p>
      )}
    </div>
  );
}

/**
 * BL-020 — a business-rule refusal is a state correction, never "try again".
 *
 * Both reviewer actions can be refused with 422 `…cannot-release` / `…cannot-return`
 * when the request has already left `AWAITING_RELEASE` — another reviewer got
 * there first. That refusal is PERMANENT: retrying is guaranteed to fail, and the
 * only reason the button is still on screen is that the local `detail` is stale.
 * The backend sends `details.status` for exactly this, so adopt it — which
 * re-renders the page for the real status and takes the control away. Returns
 * whether the refusal was handled, so the caller can fall through to the generic
 * branch when it was something else.
 */
function adoptRefusedStatus(
  result: { readonly status: number; readonly body: WireError },
  code: string,
  detail: ValuationRequestDetail,
  onReviewed: (detail: ValuationRequestDetail) => void,
): boolean {
  if (result.status !== 422 || result.body.error.code !== code) return false;

  const parsed = ValuationNotAwaitingReleaseDetailsSchema.safeParse(result.body.error.details);

  if (!parsed.success) return false;

  onReviewed({ ...detail, status: parsed.data.status });

  return true;
}

// The approval gate's operator surface (IMM-475.2). The held figures and the
// attached report filenames read read-only above the reviewer actions; the two
// actions themselves are gated on the release permission. There is deliberately no
// figure-adjustment control here — an operator who disagrees with the number is the
// party who produced it, so correcting it is a re-submit after a return.
function AwaitingReleasePanel({
  detail,
  t,
  intl,
  onReviewed,
}: {
  readonly detail: ValuationRequestDetail;
  readonly t: Translate;
  readonly intl: IntlShape;
  readonly onReviewed: (detail: ValuationRequestDetail) => void;
}) {
  const toast = useToast();
  // The two `admin-key-holder*` keys live at the PARENT namespace, unreachable
  // from this panel's `detail.*`-bound `t` — the same binding the assignee line
  // uses for the same helper.
  const tTitle = useTranslations('pages.admin.valuations');
  const held = detail.heldResult;
  // The same capability the backend resolves at the route (IMM-598): while the
  // request awaits release, only a holder of it may read the held reports.
  const canRelease = useCan('backoffice:valuation:valuation-request-release');
  const [releasing, setReleasing] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [returning, setReturning] = useState(false);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const release = async () => {
    setActionError(null);
    setReleasing(true);

    try {
      const result = await apiClient.adminValuationRequests.release({ pathParams: { id: detail.id } });

      if (result.ok) {
        toast.success(t('review.release-success'));
        onReviewed(result.body);

        return;
      }

      // BL-020: the common non-403 refusal here is the 422 the backend raises when
      // the request is no longer AWAITING_RELEASE — someone else already released
      // or returned it. That is PERMANENT, so it must not invite a retry, and
      // `details.status` is sent precisely so this stale view can self-correct.
      // Adopting it re-renders the page for the real status, which is what takes
      // „Freigeben" off the screen; the message rides a toast because this panel
      // unmounts with that adoption.
      if (adoptRefusedStatus(result, 'valuation.valuation-request.cannot-release', detail, onReviewed)) {
        toast.warning(t('review.release-conflict'));

        return;
      }

      // Any other refusal never mutates the local detail: the request stays exactly
      // as the operator sees it and only the reason is surfaced.
      setActionError(t(result.status === 403 ? 'take-on-forbidden' : 'review.release-error'));
    } catch {
      setActionError(t('review.release-error'));
    } finally {
      setReleasing(false);
    }
  };

  const returnForRework = async () => {
    const trimmed = reason.trim();

    if (trimmed === '') {
      setReasonError(t('review.return-reason-required'));

      return;
    }

    setReasonError(null);
    setActionError(null);
    setReturning(true);

    try {
      const result = await apiClient.adminValuationRequests.returnForRework({
        pathParams: { id: detail.id },
        body: { reason: trimmed },
      });

      if (result.ok) {
        setReturnOpen(false);
        setReason('');
        toast.success(t('review.return-success'));
        onReviewed(result.body);

        return;
      }

      // BL-020, same shape as `release` above: a 422 „no longer awaiting release"
      // is permanent, so adopt the status it reports instead of asking for a retry.
      if (adoptRefusedStatus(result, 'valuation.valuation-request.cannot-return', detail, onReviewed)) {
        setReturnOpen(false);
        toast.warning(t('review.return-conflict'));

        return;
      }

      setReasonError(t(result.status === 403 ? 'take-on-forbidden' : 'review.return-error'));
    } catch {
      setReasonError(t('review.return-error'));
    } finally {
      setReturning(false);
    }
  };

  return (
    <SectionCard title={t('review.title')}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {held !== null && (
          <>
            <ResultField label={t('review.price-from-label')} value={euroFormatter.format(held.priceFrom)} />
            <ResultField label={t('review.price-to-label')} value={euroFormatter.format(held.priceTo)} />
            <ResultField
              label={t('review.recommended-label')}
              value={
                held.recommendedStartPrice !== null
                  ? euroFormatter.format(held.recommendedStartPrice)
                  : t('not-provided')
              }
            />
            <ResultField
              label={t('review.valuation-date-label')}
              value={intl.formatDate(held.valuationDate, { dateStyle: 'medium' })}
            />
          </>
        )}
        {/* The PRODUCER, composed through the shared helper (IMM-594.1) so a
            machine-produced result is marked „(admin API key)" here exactly as a
            machine-held claim is on the assignee line — never rendered as the
            employee the key is bound to. */}
        <ResultField
          label={t('review.submitted-by-label')}
          value={claimHolderLabel(detail.producedByKind, detail.producedByName, tTitle) ?? t('not-provided')}
        />
        <ResultField
          label={t('review.submitted-at-label')}
          value={
            detail.submittedForApprovalAt !== null
              ? intl.formatDate(detail.submittedForApprovalAt, { dateStyle: 'medium' })
              : t('not-provided')
          }
        />
      </dl>

      {/* The backend answers 404 for an AWAITING_RELEASE report to anyone who
          cannot release it, so offering the controls to a read-only operator
          would hand them a red error banner on a page they did nothing wrong on.
          They see what they saw before: the kind and the filename. */}
      <ReportBlock requestId={detail.id} reports={detail.reports} readable={canRelease} t={t} />

      {actionError !== null && (
        <InfoBox type="danger" className="mt-4">
          {actionError}
        </InfoBox>
      )}

      <RequirePermission permission="backoffice:valuation:valuation-request-release">
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void release()} disabled={releasing || returning}>
            {releasing ? t('review.releasing') : t('review.release')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setReasonError(null);
              setReturnOpen(true);
            }}
            disabled={releasing || returning}
          >
            {t('review.return')}
          </Button>
        </div>

        <Modal
          open={returnOpen}
          onClose={() => setReturnOpen(false)}
          size="sm"
          title={t('review.return')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={() => setReturnOpen(false)} disabled={returning}>
                {t('review.return-cancel')}
              </Button>
              <Button type="button" onClick={() => void returnForRework()} disabled={returning}>
                {returning ? t('review.returning') : t('review.return-submit')}
              </Button>
            </>
          }
        >
          <div className="space-y-1">
            <Label htmlFor="return-reason">{t('review.return-reason-label')}</Label>
            <Textarea
              id="return-reason"
              maxLength={VALUATION_RETURN_REASON_MAX_LENGTH}
              invalid={reasonError !== null}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={returning}
            />
            {reasonError !== null && (
              <p role="alert" className="text-base-sm text-danger-600">
                {reasonError}
              </p>
            )}
          </div>
        </Modal>
      </RequirePermission>
    </SectionCard>
  );
}

function ReleasedResult({
  detail,
  record,
  t,
  intl,
}: {
  readonly detail: ValuationRequestDetail;
  readonly record: ValuationRecord | null;
  readonly t: Translate;
  readonly intl: IntlShape;
}) {
  return (
    <SectionCard title={t('result.title')}>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        {record !== null && (
          <>
            <ResultField label={t('result.price-from-label')} value={euroFormatter.format(record.priceFrom)} />
            <ResultField label={t('result.price-to-label')} value={euroFormatter.format(record.priceTo)} />
            <ResultField
              label={t('result.recommended-label')}
              value={
                record.recommendedStartPrice !== null
                  ? euroFormatter.format(record.recommendedStartPrice)
                  : t('not-provided')
              }
            />
            <ResultField
              label={t('result.valuation-date-label')}
              value={intl.formatDate(record.valuationDate, { dateStyle: 'medium' })}
            />
            {/* An HTML-only release files no property document, so the record
                carries no report reference and the row is simply absent. */}
            {record.reportMediaFileId !== null && (
              <ResultField label={t('result.report-label')} value={record.reportFileName ?? record.reportMediaFileId} />
            )}
          </>
        )}
        <ResultField label={t('result.released-by-label')} value={detail.releasedByName ?? t('not-provided')} />
        <ResultField
          label={t('result.released-at-label')}
          value={
            detail.releasedAt !== null ? intl.formatDate(detail.releasedAt, { dateStyle: 'medium' }) : t('not-provided')
          }
        />
      </dl>

      {/* The delivered pair, readable in place (IMM-598). `record.reports` carries
          BOTH kinds, so an HTML-only valuation — which files no property document
          and therefore has no `reportMediaFileId` row above — is no longer a blank. */}
      {record !== null && <ReportBlock requestId={detail.id} reports={record.reports} readable t={t} />}

      <p className="mt-3 text-base-sm text-text-tertiary">{t('result.note')}</p>
    </SectionCard>
  );
}

type ReportMarkup = { kind: 'idle' } | { kind: 'loading' } | { kind: 'loaded'; html: string } | { kind: 'error' };

/** The name a report is shown under, and the name its `aria-label` carries — the
 * same expression, so the accessible name never disagrees with the visible
 * filename (D8).
 */
const reportName = (report: ValuationReport): string => report.originalFilename ?? report.mediaFileId;

/**
 * ONE report block, rendered by both the approval gate and the released panel
 * (IMM-598). Per report: the kind label and filename, plus the controls that make
 * it readable — „Vorschau" / „Herunterladen" for a `DOCUMENT`, „Vollbild" /
 * „Herunterladen" for an `HTML` one — sit inside that report's OWN `<li>` (IMM-828.1),
 * so nothing but the markup itself binds a control to the file it acts on.
 *
 * Both reads go through the operator-tier leaves, which answer 404 — never 403 —
 * for a report that is not readable at this status or lies outside the caller's
 * scope. That surfaces as the inline `role="alert"`, and no retry is offered
 * because a retry cannot work (BL-020).
 *
 * `readable` mirrors the SERVER's rule rather than re-deciding it: while a request
 * awaits release only a holder of the release capability may read its reports, so
 * offering the controls to anyone else would guarantee that error rather than
 * report one. When false the block renders exactly what it rendered before this
 * ticket — the kind and the filename — and fetches nothing.
 */
function ReportBlock({
  requestId,
  reports,
  readable,
  t,
}: {
  readonly requestId: string;
  readonly reports: ReadonlyArray<ValuationReport>;
  readonly readable: boolean;
  readonly t: Translate;
}) {
  const [error, setError] = useState<string | null>(null);
  // Keyed by mode so Vorschau, Herunterladen and the HTML download never disable
  // each other — the block renders every report at once, so a shared key would
  // disable the DOCUMENT row's own download while the HTML one runs.
  const [busy, setBusy] = useState<'preview' | 'download' | 'download-html' | null>(null);
  // The expand state moves OUT of the viewer and onto the block: „Vollbild" now
  // sits in the HTML report's own row, and `UNIQUE (request, kind)` allows exactly
  // one HTML report per request, so one flag for the block is all the contract
  // permits (D6).
  const [expanded, setExpanded] = useState(false);
  const hasHtml = readable && reports.some((report) => report.kind === 'HTML');
  const markup = useReportMarkup(requestId, hasHtml);

  // `HtmlReport` unmounts the dialog (along with everything else) once the markup
  // leaves `loaded` — a `useReportMarkup` re-run on a mounted block re-enters
  // `loading` without ever calling `onExpandedChange(false)`, so `expanded` would
  // otherwise outlive the element it controls and reopen fullscreen unbidden the
  // next time the markup settles back to `loaded`.
  useEffect(() => {
    if (markup.kind !== 'loaded') setExpanded(false);
  }, [markup.kind]);

  const act = async (mode: 'preview' | 'download' | 'download-html', originalFilename?: string | null) => {
    setBusy(mode);
    setError(null);

    try {
      const opened = await runReportAction(requestId, mode, originalFilename ?? null);

      if (!opened) setError(t('report.error'));
    } catch {
      setError(t('report.error'));
    } finally {
      setBusy(null);
    }
  };

  // `ReportActionRow` sets its own `mb-2` for the block-flow layout it was
  // designed for; each `<li>` here is an `items-baseline` FLEX row instead, where
  // that margin only pushes the row down against the filename it sits beside. The
  // negative offset cancels it back to zero rather than reaching for a
  // `className` prop the row deliberately withholds.
  const rowWrapperClassName = '-mb-2 shrink-0';

  // BL-112: a report's controls are emitted by the iteration that emits the
  // report, so the pairing lives in the markup and not in the reader's head.
  const rowActions = (report: ValuationReport): ReactNode => {
    if (!readable) return null;

    if (report.kind === 'DOCUMENT') {
      return (
        <div className={rowWrapperClassName}>
          <ReportActionRow>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => void act('preview')}
              disabled={busy === 'preview'}
            >
              {t('report.preview')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-label={t('report.download-aria', { name: reportName(report) })}
              onClick={() => void act('download')}
              disabled={busy === 'download'}
            >
              {t('report.download')}
            </Button>
          </ReportActionRow>
        </div>
      );
    }

    // Offered only once the markup has loaded: while we do not yet know the
    // report can be read, neither control is invited (IMM-641.1, D5).
    if (markup.kind !== 'loaded') return null;

    return (
      <div className={rowWrapperClassName}>
        <ReportActionRow>
          <Button type="button" variant="secondary" size="sm" onClick={() => setExpanded(true)}>
            {t('report.expand')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            aria-label={t('report.download-aria', { name: reportName(report) })}
            onClick={() => void act('download-html', report.originalFilename)}
            disabled={busy === 'download-html'}
          >
            {t('report.download')}
          </Button>
        </ReportActionRow>
      </div>
    );
  };

  return (
    <div className="mt-4">
      <p className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{t('review.reports-label')}</p>
      {reports.length === 0 ? (
        <p className="mt-0.5 text-base-sm text-text-tertiary">{t('review.reports-empty')}</p>
      ) : (
        <>
          {error !== null && (
            <InfoBox type="danger" className="mt-2">
              {error}
            </InfoBox>
          )}
          {/* Each `<li>` carries both the report's identity and the controls that
              act on it — the pairing lives in the markup emitted by this one
              `.map()`, not in vertical order (IMM-828.1, BL-112). */}
          <ul className="mt-0.5 space-y-1 text-base-sm text-text-primary">
            {reports.map((report) => (
              <li key={report.kind} className="flex items-baseline justify-between gap-2 break-words">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="text-text-tertiary">{t(`review.report-kind.${report.kind}`)}</span>
                  <span className="min-w-0">{reportName(report)}</span>
                </span>
                {rowActions(report)}
              </li>
            ))}
          </ul>
          <HtmlReport markup={markup} t={t} expanded={expanded} onExpandedChange={setExpanded} />
        </>
      )}
    </div>
  );
}

/**
 * Dispatch one report action to its media helper. A pure lookup on purpose: the
 * caller's single `try` / `setError` / `setBusy` wrapper stays the block's ONE error
 * region and ONE busy scheme, which a second sibling handler would fork.
 */
async function runReportAction(
  requestId: string,
  mode: 'preview' | 'download' | 'download-html',
  originalFilename: string | null,
): Promise<boolean> {
  if (mode === 'preview') return await previewValuationReport(requestId);

  if (mode === 'download') return await downloadValuationReport(requestId);

  return await downloadValuationReportHtml(requestId, originalFilename);
}

/**
 * The HTML report rendered in place. `SandboxedHtmlViewer` splices the CSP in
 * itself, so unpoliced markup is not expressible here.
 *
 * „Vollbild" and „Herunterladen" both now sit in the report's own `<li>`
 * (IMM-828.1), so the viewer's fullscreen state is driven from here through the
 * controlled-expand seam rather than owned internally — the viewer renders no
 * action row of its own on this path.
 */
function HtmlReport({
  markup,
  t,
  expanded,
  onExpandedChange,
}: {
  readonly markup: ReportMarkup;
  readonly t: Translate;
  readonly expanded: boolean;
  readonly onExpandedChange: (expanded: boolean) => void;
}) {
  if (markup.kind === 'idle') return null;

  if (markup.kind === 'loading') return <p className="mt-3 text-base-sm text-text-tertiary">{t('report.loading')}</p>;

  if (markup.kind === 'error') {
    return (
      <p role="alert" className="mt-3 text-base-sm text-danger-600">
        {t('report.error')}
      </p>
    );
  }

  return (
    <>
      <SandboxedHtmlViewer
        title={t('report.frame-title')}
        html={markup.html}
        className="mt-3"
        closeLabel={t('report.close')}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
      <p className="mt-2 text-base-sm text-text-tertiary">{t('report.note')}</p>
    </>
  );
}

/**
 * Fetch the report markup ONCE per request and hold it above the frame. The effect
 * keys on `requestId` / `enabled` only, so a re-render for any other reason —
 * a sibling's busy flag, a revalidation that returns the same request — never
 * re-enters the loading state under a report that is already on screen (BL-011).
 */
function useReportMarkup(requestId: string, enabled: boolean): ReportMarkup {
  const [markup, setMarkup] = useState<ReportMarkup>({ kind: 'idle' });

  useEffect(() => {
    if (!enabled) {
      setMarkup({ kind: 'idle' });

      return;
    }

    let live = true;

    setMarkup({ kind: 'loading' });

    void fetchValuationReportHtml(requestId)
      .then((report) => {
        if (!live) return;

        setMarkup(report === null ? { kind: 'error' } : { kind: 'loaded', html: report });
      })
      .catch(() => {
        if (live) setMarkup({ kind: 'error' });
      });

    return () => {
      live = false;
    };
  }, [requestId, enabled]);

  return markup;
}

function ResultField({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-text-tertiary">{label}</dt>
      <dd className="mt-0.5 break-words text-base-sm text-text-primary">{value}</dd>
    </div>
  );
}

function SectionCard({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-surface px-5 py-4">
      <h2 className="mb-3 text-base-sm font-semibold text-text-secondary">{title}</h2>
      {children}
    </section>
  );
}

type Translate = (key: string, values?: Record<string, string | number>) => string;

// One rendered definition-list row. Most field keys map to exactly one; the
// `priceExpectations` marker expands into three.
type DetailRow = { readonly key: string; readonly label: string; readonly value: string };

function DetailParkingSpaces({
  detail,
  t,
  intl,
}: {
  readonly detail: ValuationRequestDetail;
  readonly t: Translate;
  readonly intl: IntlShape;
}) {
  if (detail.parkingSpaces.length === 0) return <p className="text-base-sm text-text-tertiary">{t('parking-empty')}</p>;

  return (
    <ul className="space-y-1 text-base-sm text-text-primary">
      {detail.parkingSpaces.map((entry, index) => (
        <li key={index} className="flex items-baseline gap-2">
          <span>{entry.type !== null ? t(`parkingSpaceType.${entry.type}`) : t('not-provided')}</span>
          <span className="text-text-tertiary">
            · {entry.count !== null ? intl.formatNumber(entry.count) : t('not-provided')}
          </span>
        </li>
      ))}
    </ul>
  );
}

function DetailUnits({ detail, t }: { readonly detail: ValuationRequestDetail; readonly t: Translate }) {
  if (detail.units.length === 0) return <p className="text-base-sm text-text-tertiary">{t('units-empty')}</p>;

  return (
    <ul className="space-y-2 text-base-sm text-text-primary">
      {detail.units.map((entry, index) => (
        <li key={index} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-medium">{entry.label ?? t('unit-heading', { index: index + 1 })}</span>
          {entry.lettingStatus !== null && (
            <span className="text-text-tertiary">· {t(`lettingStatus.${entry.lettingStatus}`)}</span>
          )}
          {entry.currentRent !== null && (
            <span className="text-text-tertiary">· {euroFormatter.format(entry.currentRent)}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

function DetailDocuments({
  detail,
  t,
  intl,
}: {
  readonly detail: ValuationRequestDetail;
  readonly t: Translate;
  readonly intl: IntlShape;
}) {
  // The Unterlagen block shows only the document set; the request's images are
  // their own set, rendered by `DetailImages`.
  const documents = detail.documents.filter((document) => document.kind === 'DOCUMENT');

  if (documents.length === 0) return <p className="text-base-sm text-text-tertiary">{t('documents-empty')}</p>;

  return <AttachmentList requestId={detail.id} attachments={documents} t={t} intl={intl} />;
}

function DetailImages({
  detail,
  t,
  intl,
}: {
  readonly detail: ValuationRequestDetail;
  readonly t: Translate;
  readonly intl: IntlShape;
}) {
  const images = detail.documents.filter((document) => document.kind === 'IMAGE');

  if (images.length === 0) return <p className="text-base-sm text-text-tertiary">{t('images-empty')}</p>;

  return <AttachmentList requestId={detail.id} attachments={images} t={t} intl={intl} />;
}

// Name + size + the Objekt's category, plus per-row Vorschau / Download. Both
// actions mint a short-lived presigned URL per click through the admin read leaf —
// nothing is prefetched, so opening the page does not sign a URL for every
// attachment. A failed mint is surfaced inline (the admin app carries no toast
// surface). Images reach this list too and carry no classification, so the badge
// renders nothing for them.
function AttachmentList({
  requestId,
  attachments,
  t,
  intl,
}: {
  readonly requestId: string;
  readonly attachments: ReadonlyArray<ValuationRequestDocument>;
  readonly t: Translate;
  readonly intl: IntlShape;
}) {
  const [error, setError] = useState<string | null>(null);
  // Keyed by `${documentId}:${mode}` so two rows — or preview and download on the
  // same row — never disable each other's button.
  const [busy, setBusy] = useState<string | null>(null);

  const act = async (documentId: string, mode: 'preview' | 'download') => {
    setBusy(`${documentId}:${mode}`);
    setError(null);

    try {
      const opened =
        mode === 'preview'
          ? await previewValuationDocument(requestId, documentId)
          : await downloadValuationDocument(requestId, documentId);

      if (!opened) setError(t('attachment-error'));
    } catch {
      setError(t('attachment-error'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {error !== null && (
        <InfoBox type="danger" className="mb-2">
          {error}
        </InfoBox>
      )}
      <ul className="space-y-1 text-base-sm text-text-primary">
        {attachments.map((attachment) => (
          <li key={attachment.id} className="flex items-baseline justify-between gap-2 break-words">
            <span className="min-w-0">{attachment.displayName}</span>
            <span className="flex shrink-0 items-baseline gap-3">
              <DocumentClassificationBadge
                category={attachment.category}
                categorySource={attachment.categorySource}
                classificationStatus={attachment.classificationStatus}
              />
              <span className="text-text-tertiary tabular-nums">
                {intl.formatNumber(Math.round(attachment.sizeBytes / 1024))} KB
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void act(attachment.id, 'preview')}
                disabled={busy === `${attachment.id}:preview`}
              >
                {t('attachment-preview')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => void act(attachment.id, 'download')}
                disabled={busy === `${attachment.id}:download`}
              >
                {t('attachment-download')}
              </Button>
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}
